import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/lib/constants";
import { trackEvent } from "@/lib/analytics";
import AppStoreBadge from "@/components/badges/AppStoreBadge";
import GooglePlayBadge from "@/components/badges/GooglePlayBadge";

/**
 * StoreBadges — official App Store + Google Play badges, side-by-side.
 *
 * Uses Apple's and Google's official badge artwork with NO modifications
 * to the badges themselves (per each vendor's marketing guidelines).
 * Container-level effects (hover lift, drop shadow) sit AROUND the badge,
 * not on it, which is permitted.
 *
 * Google Play badge only appears when GOOGLE_PLAY_URL is set. Until then,
 * a custom "coming soon" placeholder is shown — this is intentionally NOT
 * a modified Google Play badge (which would violate their guidelines).
 *
 * Every click fires a lightweight analytics beacon (fire-and-forget).
 *
 * @param {string} source  Where on the page this instance lives — passed
 *                         to analytics for attribution (hero/footer/etc).
 */
export default function StoreBadges({
  className = "",
  testIdPrefix = "store-badge",
  source = "unknown",
  badgeHeight = 56,
}) {
  const googlePlayLive = Boolean(GOOGLE_PLAY_URL);

  const onAppleClick = () => trackEvent("download_click", source, { store: "apple" });
  const onGoogleClick = () => trackEvent("google_play_click", source, { store: "google" });

  return (
    <div
      className={`flex flex-wrap items-center gap-4 ${className}`}
      data-testid={`${testIdPrefix}-group`}
    >
      {/* Apple / App Store — official badge, unmodified */}
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onAppleClick}
        data-testid={`${testIdPrefix}-app-store`}
        aria-label="Download Stable-ish on the App Store"
        className="inline-block transition-transform duration-300 hover:-translate-y-1 hover:drop-shadow-[0_16px_30px_rgba(255,42,42,0.35)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF2A2A]"
      >
        <AppStoreBadge height={badgeHeight} />
      </a>

      {/* Google Play — official badge when live, custom placeholder when coming-soon */}
      {googlePlayLive ? (
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onGoogleClick}
          data-testid={`${testIdPrefix}-google-play`}
          aria-label="Get Stable-ish on Google Play"
          className="inline-block transition-transform duration-300 hover:-translate-y-1 hover:drop-shadow-[0_16px_30px_rgba(255,42,42,0.35)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF2A2A]"
        >
          <GooglePlayBadge height={badgeHeight} />
        </a>
      ) : (
        <ComingSoonPlaceholder
          height={badgeHeight}
          testId={`${testIdPrefix}-google-play`}
        />
      )}
    </div>
  );
}

/**
 * Custom "Coming to Android" placeholder — intentionally NOT the official
 * Google Play badge, so we don't violate Google's brand guidelines by
 * modifying it. Matches our own dark aesthetic.
 */
function ComingSoonPlaceholder({ height, testId }) {
  // Match the 135:40 aspect ratio of Google Play so both slots read as "buttons"
  const width = (height * 135) / 40;
  return (
    <div
      data-testid={testId}
      aria-disabled="true"
      title="Coming soon to Android"
      style={{ height, width }}
      className="relative inline-flex items-center justify-center gap-3 border border-white/20 bg-white/[0.03] text-white/70 cursor-not-allowed select-none"
    >
      <span className="absolute -top-2.5 -right-2 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-white bg-[#FF2A2A] px-2 py-0.5 shadow-[0_4px_12px_-2px_rgba(255,42,42,0.6)] pointer-events-none">
        soon
      </span>
      <div className="flex flex-col leading-tight text-left px-4">
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/45">
          Coming to
        </span>
        <span className="font-semibold text-[18px] tracking-tight -mt-0.5 text-white/85">
          Android
        </span>
      </div>
    </div>
  );
}
